import React, { createContext, ReactNode, use, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { DatadogActionCreator } from '@bdc-libs/matrix.web-tracking'
import { rum } from '@divvy-web/utils.log'

import { useAuth } from '@sputnik/auth'

import { useGetDataForTracking } from './GetDataForTracking.gql'
import { createTypedTracker, HostTracker } from './webtracking'

const GITLAB_PROJECT_ID = 386
const writeKey = window.__DivvyEnvironment.SEGMENT_WRITE_KEY

export type ApplicantData = {
  aid?: string
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  legalBusinessName?: string
  companyName?: string
  numberOfEmployees?: string
  accountingSoftware?: string
  title?: string
}

type ApplicantDataQueryItem = {
  applicantData: ApplicantData
}

type DataFromUp = Record<string, unknown> & { email?: string; ltLandingPage?: string }

type WebTrackingProviderValue = {
  track: HostTracker['track']
  applicantData: ApplicantData | null
  dataFromUp: DataFromUp
}

const WebTrackingContext = createContext<WebTrackingProviderValue>({
  track: async () => {},
  applicantData: null,
  dataFromUp: {},
})

export const useWebTracking = () => use<WebTrackingProviderValue>(WebTrackingContext)

export const WebTrackingProvider = ({ children }: { children: ReactNode }) => {
  const { appId } = useParams()
  const { email } = useAuth()

  const { data } = useGetDataForTracking({
    variables: { creditApplicationId: appId || '' },
    skip: !appId,
  })

  const { creditApplication } = data || {}

  const { bdcOrgId, companyDivvyUuid, authorizedSigner, businessInfo, salesforceCreditId } = creditApplication || {}

  const { pathname, search } = useLocation()
  const applicantData: ApplicantData | null = useMemo(() => {
    try {
      if (!search) {
        return null
      }
      // we don't actually send well formed query params for all this applicant data,
      // so we cannot actually rely on normal query param parsing to grab the applicant data
      // that is sent.
      const hasActualQueryParameters = search.indexOf('&') > 0

      const decodedSearchArray: object[] = JSON.parse(
        atob(search.slice(1, hasActualQueryParameters ? search.indexOf('&') : search.length)),
      )

      const applicantDataInSearchArray = decodedSearchArray
        .filter((item): item is ApplicantDataQueryItem => !!item['applicantData'])
        .find((item) => item?.applicantData)

      if (!applicantDataInSearchArray) {
        return null
      }

      return applicantDataInSearchArray.applicantData
    } catch {
      return null
    }
  }, [search])

  const dataFromUp: DataFromUp = useMemo(() => {
    try {
      if (!search) {
        return {}
      }
      /* we don't actually send well formed query params for all this applicant data,
              so we cannot actually rely on normal query param parsing to grab the applicant data
              that is sent. */
      const hasActualQueryParameters = search.indexOf('&') > 0

      const items = JSON.parse(atob(search.slice(1, hasActualQueryParameters ? search?.indexOf('&') : search?.length)))
      const email = items?.find((item: any) => item?.email)?.email || ''
      const ltLandingPage = items?.find((item: any) => item?.lt)?.lt?.landingPage || ''

      return { ...items, email, ltLandingPage }
    } catch {
      return {}
    }
  }, [search])

  const trackerInstance = useMemo(() => {
    if (!writeKey) {
      return
    }

    return createTypedTracker({
      writeKey,
      datadogActionCreator: new DatadogActionCreator(rum),
    })
  }, [])

  useEffect(() => {
    if (writeKey && trackerInstance) {
      if (applicantData?.aid) {
        trackerInstance.setAnonymousId(applicantData?.aid)
      }
    }
  }, [applicantData, trackerInstance])

  useEffect(() => {
    if (writeKey && trackerInstance) {
      if (applicantData) {
        trackerInstance.identify({
          email: applicantData?.email || dataFromUp?.email,
          first_name: applicantData?.firstName,
          last_name: applicantData?.lastName,
          signup_source: dataFromUp?.ltLandingPage,
          _bdc_org_id: bdcOrgId || undefined,
          _divvy_org_uuid: companyDivvyUuid || undefined,
        })
      } else if (email || creditApplication || dataFromUp?.email) {
        const getUserInfo = () => {
          if (creditApplication && email) {
            if (email === authorizedSigner?.email) {
              return {
                userRole: `authorizedSigner - ${authorizedSigner?.title}`,
                firstName: authorizedSigner?.firstName,
                lastName: authorizedSigner?.lastName,
              }
            }
            if (email === creditApplication?.admin?.email) {
              return {
                userRole: 'admin',
                firstName: creditApplication?.admin?.firstName,
                lastName: creditApplication?.admin?.lastName,
              }
            }
            if (creditApplication?.beneficialOwners?.find((owner: any) => email === owner?.email)) {
              const owner = creditApplication?.beneficialOwners?.find((owner: any) => email === owner?.email)
              return {
                userRole: 'companyOwner',
                firstName: owner?.firstName,
                lastName: owner?.lastName,
              }
            }
            if (email === creditApplication?.outsourcedAccountant?.email) {
              return {
                userRole: 'outsourcedAccountant',
                firstName: creditApplication?.outsourcedAccountant?.firstName,
                lastName: creditApplication?.outsourcedAccountant?.lastName,
              }
            }
            return { userRole: 'collaborator' }
          }
          return null
        }

        trackerInstance.identify({
          email: email || dataFromUp?.email,
          first_name: getUserInfo()?.firstName || undefined,
          last_name: getUserInfo()?.lastName || undefined,
          business_type:
            businessInfo?.entityType && businessInfo?.industry
              ? `${businessInfo?.entityType} - ${businessInfo?.industry}`
              : undefined,
          signup_source: dataFromUp?.ltLandingPage,
          user_role: getUserInfo()?.userRole || undefined,
          address_country: businessInfo?.physicalAddress?.countryCode || undefined,
          address_zipcode: businessInfo?.physicalAddress?.postalCode || undefined,
          _bdc_org_id: bdcOrgId || undefined,
          _divvy_org_uuid: companyDivvyUuid || undefined,
        })
      }
    }
  }, [
    applicantData,
    trackerInstance,
    email,
    authorizedSigner,
    businessInfo?.entityType,
    businessInfo?.industry,
    businessInfo?.physicalAddress?.countryCode,
    businessInfo?.physicalAddress?.postalCode,
    bdcOrgId,
    companyDivvyUuid,
    creditApplication,
    dataFromUp?.email,
    dataFromUp?.ltLandingPage,
    salesforceCreditId,
  ])

  useEffect(() => {
    if (writeKey && trackerInstance && (bdcOrgId || companyDivvyUuid)) {
      trackerInstance.group(companyDivvyUuid || '', {
        accounting_software:
          applicantData?.accountingSoftware ?? businessInfo?.accountingSoftware?.accountingSoftwareKey ?? undefined,
        _bdc_org_id: bdcOrgId || undefined,
        _divvy_org_uuid: companyDivvyUuid || undefined,
      })
    }
  }, [bdcOrgId, companyDivvyUuid, businessInfo?.accountingSoftware, trackerInstance, applicantData])

  useEffect(() => {
    if (writeKey && trackerInstance) {
      trackerInstance.init({
        _app_host_name: 'sputnik',
        _app_host_version: window?.__DivvyEnvironment?.DEPLOY_VERSION,
        _project_id: GITLAB_PROJECT_ID,
        _bdc_org_id: bdcOrgId || undefined,
        _divvy_org_uuid: companyDivvyUuid || undefined,
      })
    }
  }, [trackerInstance, bdcOrgId, companyDivvyUuid])

  // PAGE call
  useEffect(() => {
    if (writeKey && trackerInstance) {
      const pathSegments = pathname.split('/').filter(Boolean)
      const pageName = pathSegments[pathSegments.length - 1]
      if (pageName) {
        trackerInstance.page('page_load', pageName)
      }
    }
  }, [pathname, trackerInstance])

  const track = useMemo(() => trackerInstance?.track ?? (async () => {}), [trackerInstance?.track])

  return <WebTrackingContext value={{ track, applicantData, dataFromUp }}>{children}</WebTrackingContext>
}
